import { useMemo, useState } from 'react'
import { useHazardLibrary, useCreateRisksFromLibrary } from '../hooks/useRisks'
import { ModalShell, CancelButton, PrimaryButton, ServerError } from './modal-form'
import { scoreBand } from './RiskEditor'

// HazardLibraryPickerModal - bulk-add common hazards from the org library to
// a per-event risk register. Filterable by category; checkbox list; "Add N
// to event" button at the bottom shows the running count.

interface Props {
  eventId: number
  onClose: () => void
}

export function HazardLibraryPickerModal({ eventId, onClose }: Props) {
  const { data: library, isLoading, error } = useHazardLibrary()
  const apply = useCreateRisksFromLibrary(eventId)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [filter, setFilter] = useState<string>('all')

  const filtered = useMemo(() => {
    const list = library ?? []
    if (filter === 'all') return list
    return list.filter((h) => h.category === filter)
  }, [library, filter])

  const categories = useMemo(() => {
    const set = new Set<string>()
    for (const h of library ?? []) set.add(h.category)
    return ['all', ...Array.from(set).sort()]
  }, [library])

  function toggle(id: number) {
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleAdd() {
    if (selected.size === 0) return
    apply.mutate(Array.from(selected), { onSuccess: () => onClose() })
  }

  const body = (
    <div className="space-y-3">
      {isLoading && <p className="text-sm text-ink-soft font-mono">Loading library…</p>}
      {error && <ServerError title="Could not load library" error={error as Error} />}

      {library && library.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                className={
                  'px-2.5 py-1 text-xs font-semibold rounded-full border transition-colors ' +
                  (filter === c
                    ? 'bg-green text-parchment border-green'
                    : 'bg-white text-ink-soft border-line hover:border-green')
                }
              >
                {c === 'all' ? 'All categories' : c}
              </button>
            ))}
          </div>

          <div className="space-y-1 max-h-[55vh] overflow-y-auto border border-line rounded">
            {filtered.map((h) => {
              const checked = selected.has(h.id)
              const score = h.defaultLikelihood * h.defaultSeverity
              const band = scoreBand(score)
              return (
                <label
                  key={h.id}
                  className={
                    'flex items-start gap-3 p-3 cursor-pointer transition-colors border-b border-line last:border-b-0 ' +
                    (checked ? 'bg-green-soft/40' : 'hover:bg-parchment')
                  }
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(h.id)}
                    className="mt-1 accent-green"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="text-sm font-semibold">{h.hazard}</span>
                      <span className="text-[10px] text-ink-soft uppercase tracking-wide">
                        {h.category}
                      </span>
                    </div>
                    {h.suggestedControls && (
                      <p className="text-xs text-ink-soft">{h.suggestedControls}</p>
                    )}
                  </div>
                  <span
                    className="font-mono text-xs font-bold px-2 py-0.5 rounded flex-shrink-0"
                    style={{ background: band.bg, color: band.fg }}
                    title={`Default L=${h.defaultLikelihood} × S=${h.defaultSeverity}`}
                  >
                    {score}
                  </span>
                </label>
              )
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-4 text-xs italic text-ink-soft">
                No hazards match the current filter.
              </p>
            )}
          </div>
        </>
      )}

      {apply.error && <ServerError title="Could not add hazards" error={apply.error as Error} />}
    </div>
  )

  const footer = (
    <>
      <CancelButton onClick={onClose} disabled={apply.isPending} />
      <PrimaryButton
        onClick={handleAdd}
        disabled={selected.size === 0 || apply.isPending}
        type="button"
      >
        {apply.isPending
          ? 'Adding…'
          : selected.size === 0
          ? 'Add to event'
          : `Add ${selected.size} to event`}
      </PrimaryButton>
    </>
  )

  return (
    <ModalShell
      title="Add hazards from library"
      onClose={onClose}
      body={body}
      footer={footer}
      size="2xl"
      onSubmit={(e) => {
        e.preventDefault()
        handleAdd()
      }}
    />
  )
}
